'use client';
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useReviewService } from '@/hooks/review/useReviewService';
import { PRODUCT_QUERIES } from '@/infrastructure/product/utils/queries';
import { useRegionCurrency } from '@/contexts/regionCurrencyContext';
import type { Review } from '@/models/review/review';
import CustomerTestimonialsSwiperSkeleton from './CustomerTestimonialsSwiperSkeleton';
import { Carousel, type CarouselApi } from '@/components/ui/Carousel';
import TestimonialCard from './TestimonialCard';
import CarouselNavigation from './CarouselNavigation';
import { extractProductIdsFromReviews, createProductMap } from '../utils/productUtils';
import { getNormalizedCarouselIndex, getCarouselItemBorderConditions } from '../utils/carouselUtils';

const CustomerTestimonialsSwiper = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const { useGetCuratedReviews } = useReviewService();
  const { currency } = useRegionCurrency();
  const { data: reviewData } = useGetCuratedReviews();
  const reviews = useMemo(() => reviewData || [], [reviewData]);

  const productIds = useMemo(() => extractProductIdsFromReviews(reviews), [reviews]);

  const { data: productData } = useQuery(PRODUCT_QUERIES.getProductsByIds({ ids: productIds, currency }));

  const productMap = useMemo(() => {
    if (!productData) {
      return {};
    }
    return createProductMap(productData);
  }, [productData]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const handleSelect = () => {
      setActiveIndex(api.selectedScrollSnap());
    };

    api.on('select', handleSelect);
    api.on('reInit', handleSelect);
    handleSelect();

    return () => {
      api.off('select', handleSelect);
    };
  }, [api]);

  return (
    <Box width="100%" position="relative">
      <Flex position="relative" width="100%" overflow="hidden">
        <Carousel
          setApi={setApi}
          opts={{
            loop: true,
            align: 'start',
            skipSnaps: false,
          }}
          w="100%"
        >
          <Carousel.Content>
            {reviews.slice(0, 5).map((review: Review, index: number) => {
              const product = productMap[review.productId];
              const totalCards = reviews.slice(0, 5).length;
              const normalizedIndex = getNormalizedCarouselIndex(index, activeIndex, totalCards);
              const { isMiddleCard, isFirstCardOnTablet } = getCarouselItemBorderConditions(normalizedIndex);

              return (
                <Carousel.Item
                  key={`${review.reviewId}-${index}`}
                  flexBasis={{
                    base: '100%',
                    tablet: '50%',
                    desktop: '33.333%',
                  }}
                  paddingX={6}
                  borderLeft={{
                    base: 'none',
                    tablet: 'none',
                    desktop: isMiddleCard ? '1px solid' : 'none',
                  }}
                  borderRight={{
                    base: 'none',
                    tablet: isFirstCardOnTablet ? '1px solid' : 'none',
                    desktop: isMiddleCard ? '1px solid' : 'none',
                  }}
                  borderLeftColor={{
                    base: 'transparent',
                    tablet: 'transparent',
                    desktop: isMiddleCard ? 'neutral.10' : 'transparent',
                  }}
                  borderRightColor={{
                    base: 'transparent',
                    tablet: isFirstCardOnTablet ? 'neutral.10' : 'transparent',
                    desktop: isMiddleCard ? 'neutral.10' : 'transparent',
                  }}
                >
                  <TestimonialCard review={review} product={product} />
                </Carousel.Item>
              );
            })}
          </Carousel.Content>
        </Carousel>
      </Flex>

      <CarouselNavigation api={api} activeIndex={activeIndex} totalItems={Math.min(reviews.length, 5)} />
    </Box>
  );
};

const CustomerTestimonialsSwiperWithSkeleton = CustomerTestimonialsSwiper as typeof CustomerTestimonialsSwiper & {
  Skeleton: typeof CustomerTestimonialsSwiperSkeleton;
};

CustomerTestimonialsSwiperWithSkeleton.Skeleton = CustomerTestimonialsSwiperSkeleton;

export default CustomerTestimonialsSwiperWithSkeleton;
